import React, { useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Button from "../../components/ui/Button";
import TextField from "@mui/material/TextField";
import { host } from "../../api/ApiCalls";
import { tostifyOption } from "../../utils/toastOptions";

const ForgetPasswordDialog = ({ open, onClose }) => {
  const [step, setStep] = useState(1);
  const [username, setUsername] = useState("");
  const [otp, setOtp] = useState("");
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [email, setEmail] = useState("");
  const [sendOtpLoading, setSendOtpLoading] = useState(false);
  const [verifyOtpLoading, setVerifyOtpLoading] = useState(false);
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);

  // Send OTP
  const handleSendOTP = async () => {
    setSendOtpLoading(true);
    try {
      const res = await axios.post(
        `${host}/api/auth/forgot-password/send-otp`,
        { username }
      );
      if (res.status === 200) {
        setEmail(res.data.email);
        toast.success(res.data.message, tostifyOption);
        setStep(2); // Move to OTP entry step
      } else {
        toast.error(res.data.message, tostifyOption);
      }
      setSendOtpLoading(false);
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to send OTP",
        tostifyOption
      );
    }
    setSendOtpLoading(false);
  };

  // Verify OTP
  const handleVerifyOTP = async () => {
    setVerifyOtpLoading(true);
    try {
      await axios.post(`${host}/api/auth/forgot-password/verify-otp`, {
        username,
        otp,
      });
      toast.success("OTP Verified Successfully!");
      setStep(3); // Move to password reset step
      setVerifyOtpLoading(false);
    } catch (error) {
      toast.error(error.response?.data?.message || "Invalid OTP");
      setVerifyOtpLoading(false);
    }
  };

  // Reset Password
  const handleResetPassword = async () => {
    setResetPasswordLoading(true);
    const passwordPattern =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

      if (!passwordPattern.test(newPassword)) {
            toast.error(
              'Password must have at least 8 chars, 1 uppercase, 1 number, and 1 special character.',
              tostifyOption
            );
            setResetPasswordLoading(false);
            return;
          }
    try {
      await axios.put(`${host}/api/auth/forgot-password/reset`, {
        username,
        newPassword,
      });
      toast.success("Password updated successfully!");
      onClose(); // Close dialog after success
      setStep(1); // Reset step to initial state
      setUsername("");
      setEmail("");
      setOtp("");
      setNewPassword("");
      setResetPasswordLoading(false);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update password");
      setResetPasswordLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 400,
          bgcolor: "white",
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
        }}
      >
        {step === 1 && (
          <>
            <h2>Forgot Password</h2>
            <input
              type="text"
              required
              placeholder="Enter your Username"
              className="my-5 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              onChange={(e) => setUsername(e.target.value)}
            />

            <Button
              type="submit"
              className="w-full bg-indigo-700"
              onClick={handleSendOTP}
              disabled={sendOtpLoading}
            >
              {sendOtpLoading ? (
                <div className="flex items-center justify-center gap-2">
                  <svg
                    className="animate-spin h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"
                    />
                  </svg>
                  Sending...
                </div>
              ) : (
                "Send OTP"
              )}
            </Button>
          </>
        )}

        {step === 2 && (
          <>
            <h2 className="text-center">
              OTP has sent to {username}. Check Your email: {email}
            </h2>

            <input
              required
              placeholder="Enter OTP"
              className="my-5 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              onChange={(e) => setOtp(e.target.value)}
              value={otp}
            />

            <Button
              type="submit"
              className="w-full bg-indigo-700"
              onClick={handleVerifyOTP}
              disabled={verifyOtpLoading}
            >
              {verifyOtpLoading ? (
                <div className="flex items-center justify-center gap-2">
                  <svg
                    className="animate-spin h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"
                    />
                  </svg>
                  Verifying...
                </div>
              ) : (
                "Verify OTP"
              )}
            </Button>
          </>
        )}

        {step === 3 && (
          <>
            <h2>Reset Password</h2>
            <input
              type="password"
              required
              minLength={8}
              placeholder="Enter your New Password"
              className="my-5 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              onChange={(e) => setNewPassword(e.target.value)}
              value={newPassword}
            />

            <Button
              type="submit"
              className="w-full bg-indigo-700"
              onClick={handleResetPassword}
              disabled={resetPasswordLoading}
            >
              {resetPasswordLoading ? (
                <div className="flex items-center justify-center gap-2">
                  <svg
                    className="animate-spin h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"
                    />
                  </svg>
                  Updating...
                </div>
              ) : (
                "Update Password"
              )}
            </Button>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default ForgetPasswordDialog;
